import {
  flexRender,
  RowData,
  Table as TableType,
  ColumnPinningState,
} from "@tanstack/react-table";
import * as React from "react";

import { cn } from "../../lib/utils";
import {
  NestedSurfaceBg,
  resolveNestedSurfaceBg,
} from "../../lib/resolve-nested-surface-bg";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./table";
import { DataTablePagination } from "./pagination";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    style?: {
      padding?: string | number;
      background?: string;
    };
    className?: string;
  }
}

interface DataTableProps<TData extends RowData> {
  onPageChange?: (page: number) => void;
  className?: string;
  disabled?: boolean;
  table: TableType<TData>;
  pagination?: boolean;
  noHeader?: boolean;
  columnPinning?: ColumnPinningState;
  headerClassName?: string;
  emptyState?: React.FC;
  topScroller?: React.ReactNode;
  bottomScroller?: React.ReactNode;
}

export function DataTable<TData extends RowData>({
  className,
  disabled,
  table,
  pagination = true,
  noHeader = false,
  headerClassName,
  emptyState: EmptyState,
}: DataTableProps<TData>) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [headerBg, setHeaderBg] = React.useState<NestedSurfaceBg>("bg-muted");

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    setHeaderBg(resolveNestedSurfaceBg(container.parentElement));
  }, [className]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "flex flex-col border rounded-lg border-border relative w-full",
        disabled && "opacity-50",
        className
      )}
    >
      <Table className={cn("w-full")}>
        {!noHeader && (
          <TableHeader className={cn("sticky top-0 z-10", headerClassName)}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead
                      className={cn(
                        "group first:rounded-tl-lg last:rounded-tr-lg relative",
                        headerBg,
                        header.column.getIsPinned() &&
                          cn("sticky z-5", headerBg),
                        header.column.getIsPinned() === "left" && "left-0",
                        header.column.getIsPinned() === "right" && "right-0"
                      )}
                      key={header.id}
                      colSpan={header.colSpan}
                      style={{
                        width:
                          header.column.columnDef.size ===
                          Number.MAX_SAFE_INTEGER
                            ? "auto"
                            : header.column.columnDef.size,
                        maxWidth:
                          header.column.columnDef.size ===
                          Number.MAX_SAFE_INTEGER
                            ? "none"
                            : header.column.columnDef.maxSize,
                        minWidth:
                          header.column.columnDef.minSize ===
                          Number.MAX_SAFE_INTEGER
                            ? "none"
                            : header.column.columnDef.minSize,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      <div
                        tabIndex={-1}
                        role="gridcell"
                        onDoubleClick={() => header.column.resetSize()}
                        onMouseDown={header.getResizeHandler()}
                        onTouchStart={header.getResizeHandler()}
                        className={cn(
                          "resizer hidden group-hover:block",
                          table.options.columnResizeDirection,
                          header.column.getIsResizing() && "isResizing"
                        )}
                      />
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
        )}
        <TableBody className="py-16 overflow-x-auto size-full">
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className={cn(
                row.getIsSelected() && "selected",
                !row.getCanSelect() && "hover:bg-background"
              )}
              onClick={row.getToggleSelectedHandler()}
              data-state={row.getIsSelected() && "selected"}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  className={cn(
                    "text-foreground",
                    cell.column.getIsPinned() && "sticky z-5 bg-background",
                    cell.column.getIsPinned() === "left" && "left-0",
                    cell.column.getIsPinned() === "right" && "right-0",
                    cell.column.columnDef.meta?.className,
                    row.getIsSelected() ? "bg-background" : ""
                  )}
                  key={cell.id}
                  style={{
                    width:
                      cell.column.columnDef.size === Number.MAX_SAFE_INTEGER
                        ? "auto"
                        : cell.column.columnDef.size,
                    maxWidth:
                      cell.column.columnDef.size === Number.MAX_SAFE_INTEGER
                        ? "none"
                        : cell.column.columnDef.maxSize,
                    minWidth:
                      cell.column.columnDef.minSize === Number.MAX_SAFE_INTEGER
                        ? "none"
                        : cell.column.columnDef.minSize,
                    ...(cell.column.columnDef.meta?.style ?? {}),
                  }}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {table.getRowModel().rows?.length === 0 &&
        (EmptyState ? (
          <EmptyState />
        ) : (
          <div className="text-muted-foreground pt-12 text-sm size-full top-0 absolute flex flex-col items-center justify-center">
            No results.
          </div>
        ))}
      {pagination && (
        <DataTablePagination
          className={cn("rounded-b-lg bottom-0 z-20 w-full")}
          table={table}
        />
      )}
    </div>
  );
}
